import { useCallback, useEffect, useRef, useState } from "react";

const ROOT = "overview";

function makeBase() {
  return { stack: [ROOT], overlay: null };
}

// Owns the screen stack + overlay signal and mirrors it into browser history so
// that back/forward gestures walk the same path as the in-app chevrons.
export default function useScreenStack() {
  const [nav, setNav] = useState(makeBase);
  const navRef = useRef(nav);
  const sidRef = useRef(Math.random().toString(36).slice(2));

  const apply = useCallback((next) => {
    navRef.current = next;
    setNav(next);
  }, []);

  const write = useCallback((next, push) => {
    const entry = { execNav: { ...next, sid: sidRef.current } };
    const url = push
      ? `#${next.overlay || next.stack[next.stack.length - 1]}`
      : window.location.pathname;
    if (push) {
      window.history.pushState(entry, "", url);
    } else {
      window.history.replaceState(entry, "", url);
    }
    apply(next);
  }, [apply]);

  useEffect(() => {
    // Claim the current history entry on mount (also strips any stale hash
    // left behind from a previous session's deep stack).
    write(makeBase(), false);

    const onPopState = (e) => {
      const state = e.state?.execNav;
      if (state && state.sid === sidRef.current && Array.isArray(state.stack)) {
        apply({ stack: state.stack, overlay: state.overlay ?? null });
      } else {
        apply(makeBase());
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pushScreen = useCallback((id) => {
    const prev = navRef.current;
    write({ stack: [...prev.stack.filter((s) => s !== id), id], overlay: null }, true);
  }, [write]);

  const openOverlay = useCallback((name) => {
    const prev = navRef.current;
    if (prev.overlay === name) return;
    write({ ...prev, overlay: name }, true);
  }, [write]);

  const goBack = useCallback(() => {
    window.history.back();
  }, []);

  const resetNav = useCallback(() => {
    write(makeBase(), false);
  }, [write]);

  return {
    screenStack: nav.stack,
    overlay: nav.overlay,
    pushScreen,
    openOverlay,
    goBack,
    resetNav,
  };
}
